// src/pages/landingpage.js - Complete Frontend Implementation with Anthropic-style SSE Streaming
import React, { useRef, useEffect, useState, useContext, useCallback } from 'react';
import { AppContext } from '../context/AppContext';
import * as d3 from 'd3';
import { v4 as uuidv4 } from 'uuid';
import { mainWorkflow } from '../components/mainWorkflow.js';
import TopBar from '../components/TopBar';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import chalk from 'chalk';
import ChainedLM0 from '../assets/ChainedLM0.jpeg';
import { audio, header } from 'framer-motion/client';
import threedots from '../assets/threedots.gif';
import BreathingIcon from '../components/BreathingIcon';
import HeroShowcase from '../components/HeroShowcase';
import InlineOutreach from '../components/InlineOutreach';
import BreathingIconStatic from '../components/BreathingIconStatic';
import LogViewer from '../components/LogViewer';
import deepseek from '../assets/deepseek.png';
import gemini from '../assets/gemini.png';
import claude from '../assets/claude.png';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Fab from '@mui/material/Fab';
import CircularProgress from '@mui/material/CircularProgress';
import DoneIcon from '@mui/icons-material/Done';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import ArrowUpwardSharpIcon from '@mui/icons-material/ArrowUpwardSharp';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import Button from '@mui/material/Button';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import HttpsOutlinedIcon from '@mui/icons-material/HttpsOutlined';
import { Lock, X } from 'lucide-react';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import { AppContextisChecked } from '../context/AppContextisChecked';
import BasicModal from '../components/BasicModal.js';
import { UserAuth } from '../context/AuthContext';
import { auth } from '../firebase/firebaseConfig';
import { useConversations } from '../context/ConversationContext';

// A restored conversation is never actively streaming, but it may have been persisted mid-stream
// with loading:true / an in-progress stage — which leaves the spinner + "Generating Initial Report"
// hanging forever. Normalise restored sections to a settled state.
function normalizeRestoredSections(sections) {
    return (Array.isArray(sections) ? sections : []).map((s) => {
        if (!s || typeof s !== 'object') return s;
        const its = s.iterations;
        const itArr = Array.isArray(its) ? its : (its && typeof its === 'object' ? Object.values(its) : []);
        const hasReport = !!s.finalSummary || itArr.some((it) => it && (it.finalSummary || it.initialAnalysis));
        const stage = s.stage === 'Completed' || s.stage === 'Error' || s.stage === 'Stopped'
            ? s.stage
            : (hasReport ? 'Completed' : s.stage);
        return { ...s, loading: false, stage };
    });
}

function Landingpage() {
    // All state variables
    const [loadingarticles, setLoadingarticles] = useState(false);
    const [errorarticles, setErrorarticles] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [inputOperator, setInputOperator] = useState('');
    const [lastQuery, setLastQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingOperator, setIsLoadingOperator] = useState(false);
    const [searchStatus, setSearchStatus] = useState('');
    const [error, setError] = useState(null);
    const [currentSearchResults, setCurrentSearchResults] = useState([]);
    const [showTavilyModal, setShowTavilyModal] = useState(false);
    const [showReasoningModal, setShowReasoningModal] = useState(false);
    const [selectedMessageData, setSelectedMessageData] = useState({});
    const [hasSubmitted1, setHasSubmitted1] = useState(false);
    const [hasSubmitted2, setHasSubmitted2] = useState(false);
    const [hidearticleshook, setHidearticleshook] = useState(false);
    const [viewInputButton, setViewInputButton] = useState(false);
    const abortControllerRef = useRef(null);
    const [chatSections, setChatSections] = useState([]);
    const [selectedSuggestion, setSelectedSuggestion] = useState(null);
    const [articleshook, setArticleshook] = useState();
    const [finalSummaryhook, setFinalSummaryhook] = useState('');
    const [subtopicshook, setSubtopicshook] = useState('');
    const [iter1HookState, setIter1Hook] = useState('');
    const [thinkingObjState, setThinkingobj] = useState({});
    const [first_img, setFirst_img] = useState('');
    const [second_img, setSecond_img] = useState('');
    const bottomRef = useRef(null);
    const activeRequestRef = useRef(null);
    const esRef = useRef(null); // NEW: EventSource reference
    const [stream, setStream] = useState(false);
    const [logEntries, setLogEntries] = useState({});
    const [passedinput, setPassedinput] = useState('');
    const [stopButton, setStopButton] = useState(false);
    const [showDeepSearchTooltip, setShowDeepSearchTooltip] = useState(false);
    const [showThinkTooltip, setShowThinkTooltip] = useState(false);
    const [showSearchwebTooltip, setShowSearchwebTooltip] = useState(false);
    const [showuseCredentialsTooltip, setShowuseCredentialsTooltip] = useState(false);
    const [profileCountChange, setProfileCountChange] = useState(5);
    const [isMobile, setIsMobile] = useState(false);
    const [isChecked, setIsChecked] = useState(() => {
        try {
            const storedValue = localStorage.getItem('isChecked');
            return storedValue !== null ? JSON.parse(storedValue) : false;
        } catch (e) {
            console.error('Error parsing isChecked from localStorage:', e);
            return false;
        }
    });
    const [currentRequestId, setCurrentRequestId] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [globalRequestRunning, setGlobalRequestRunning] = useState(false);
    const [requestGuardMessage, setRequestGuardMessage] = useState('');
    const requestGuardTimerRef = useRef(null);

    const {
        AI_Agent_Model_apiProvider, setAI_Agent_Model_apiProvider,
        AI_Agent_Model_apiModel, setAI_Agent_Model_apiModel,
        AI_Agent_Model_apikey, setAI_Agent_Model_apikey
    } = useContext(AppContext);

    const { auth, setAuth } = useContext(AppContextisChecked);
    const { unpaid, setUnpaid } = useContext(AppContextisChecked);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [focused, setFocused] = useState(false);
    const { user, loading: authLoading } = UserAuth();
    const showAuthModal = !user || authLoading;
    const conversation = useConversations(); // chat-history sidebar (MongoDB)
    // Keep a live snapshot of the rendered output so it can be persisted/replayed.
    const chatSectionsRef = useRef([]);
    useEffect(() => { chatSectionsRef.current = chatSections; }, [chatSections]);
    const messagesRef = useRef([]);
    useEffect(() => { messagesRef.current = messages; }, [messages]);
    const activeConversationKeyRef = useRef(null);
    const activeStreamConversationKeyRef = useRef(null);
    const conversationDraftsRef = useRef(new Map());
    // Per-TAB "start a new chat section" flag. Was previously a shared localStorage
    // key ('isEnabled'), which leaked between browser tabs; a ref keeps each tab independent.
    const newSectionFlagRef = useRef(false);
    // Per-CONVERSATION request tracking (Claude-style): each conversation/tab owns its
    // own request, so stopping one never touches another. Keyed by conversation key
    // (migrates from `pending:<id>` to the real id alongside the draft).
    const streamControllersRef = useRef(new Map()); // convKey -> AbortController
    const streamEsRef = useRef(new Map());          // convKey -> EventSource
    const streamRequestIdRef = useRef(new Map());   // convKey -> requestId
    useEffect(() => () => {
        if (requestGuardTimerRef.current) {
            clearTimeout(requestGuardTimerRef.current);
        }
    }, []);

    // Let the sidebar reset (New chat) and reload (open a past chat) this view.
    useEffect(() => {
        if (!conversation?.registerChatHandlers) return;
        conversation.registerChatHandlers({
            resetChat: () => {
                activeConversationKeyRef.current = null;
                setMessages([]);
                setChatSections([]);
                setThinkingobj({});
                setHasSubmitted1(false);
                setInput('');
                setFinalSummaryhook('');
                setSubtopicshook('');
                setError(null);
                setIsLoading(false);
            },
            loadConversation: (convo) => {
                activeConversationKeyRef.current = convo?._id || null;
                const draft = convo?._id ? conversationDraftsRef.current.get(convo._id) : null;
                // Only restore from the in-memory draft when it actually has something to show
                // (rendered sections, messages, or an in-flight stream). An empty/stale draft must
                // NOT shadow the persisted conversation — that's what caused the occasional blank.
                const draftHasContent = draft && (
                    draft.isLoading ||
                    (Array.isArray(draft.chatSections) && draft.chatSections.length > 0) ||
                    (Array.isArray(draft.messages) && draft.messages.length > 0)
                );
                if (draftHasContent) {
                    setMessages(draft.messages || []);
                    // If the conversation isn't actively streaming, settle any mid-stream sections
                    // so the spinner doesn't hang; otherwise keep the live (loading) sections as-is.
                    setChatSections(draft.isLoading ? (draft.chatSections || []) : normalizeRestoredSections(draft.chatSections));
                    setThinkingobj(draft.thinkingObj || {});
                    setHasSubmitted1(Boolean((draft.messages || []).length || (draft.chatSections || []).length));
                    setError(null);
                    setIsLoading(Boolean(draft.isLoading));
                    setIsProcessing(Boolean(draft.isProcessing));
                    return;
                }

                const msgs = Array.isArray(convo?.messages) ? convo.messages : [];
                setMessages(msgs.map((m) => ({ role: m.role, content: m.content })));
                // Restore the richest stored render (last assistant payload with sections).
                let restored = [];
                for (const m of msgs) {
                    if (m.role === 'assistant' && m.payload && Array.isArray(m.payload.chatSections)) {
                        restored = m.payload.chatSections;
                    }
                }
                setChatSections(normalizeRestoredSections(restored));
                setThinkingobj({});
                setHasSubmitted1(msgs.length > 0);
                setError(null);
                setIsLoading(false);
            }
        });
    }, [conversation?.registerChatHandlers]);

    const {
        apiError, setAPIError,
        Actor_Model_apiProvider, setActor_Model_apiProvider,
        Critic_Model_apiProvider, setCritic_Model_apiProvider,
        Actor_Model_apiModel, setActor_Model_apiModel,
        Critic_Model_apiModel, setCritic_Model_apiModel,
        temperature_val_Actor_Model_apiProvider, setTemperature_val_Actor_Model_apiProvider,
        temperature_val_Critic_Model_apiProvider, setTemperature_val_Critic_Model_apiProvider
    } = useContext(AppContext);

    const [useCredentials, setUseCredentials] = useState(false);
    const [screenshotUrl, setScreenshotUrl] = useState(null);
    const [cachedScreenshotUrl, setCachedScreenshotUrl] = useState(null);
    const [stopScreenshotUrl, setStopScreenshotUrl] = useState(false);
    const [isSendHovered, setIsSendHovered] = useState(false);
    const [DeepSearch, setDeepSearch] = useState(false);
    const [Think, setThink] = useState(false);
    const [Searchweb, setSearchweb] = useState(false);
    const [changeLogViewer_pos, setChangeLogViewer_pos] = useState(false);

    let thinkingobj = {};
    let iter1Hook = '';

    // Helper function to patch or append chat sections
    function patchOrAppendChatSections(prevChats, newObj, keysToAdd) {
        console.log('[LANDINGPAGE] patchOrAppendChatSections called:', {
            prevChatsLength: prevChats.length,
            newObjKeys: Object.keys(newObj || {}),
            keysToAddKeys: Object.keys(keysToAdd || {}),
            iterations: newObj?.iterations
        });

        if (prevChats.length === 0) {
            newSectionFlagRef.current = false;
            return [newObj];
        }
        if (newSectionFlagRef.current === true) {
            const updated = [...prevChats];
            updated.push(newObj);
            newSectionFlagRef.current = false;
            return updated;
        }
        const updated = [...prevChats];
        const lastIndex = updated.length - 1;
        const lastElem = updated[lastIndex];
        updated[lastIndex] = { ...lastElem, ...keysToAdd };

        console.log('[LANDINGPAGE] patchOrAppendChatSections result:', {
            updatedIterations: updated[lastIndex].iterations
        });

        return updated;
    }

    function saveConversationDraft(conversationKey, patch) {
        if (!conversationKey) return;
        const existing = conversationDraftsRef.current.get(conversationKey) || {};
        conversationDraftsRef.current.set(conversationKey, { ...existing, ...patch });
    }

    function migrateConversationDraft(oldKey, newKey) {
        if (!oldKey || !newKey || oldKey === newKey) return;
        const draft = conversationDraftsRef.current.get(oldKey);
        if (draft) {
            const migratedDraft = {
                ...draft,
                chatSections: Array.isArray(draft.chatSections)
                    ? draft.chatSections.map(section => ({ ...section, conversationKey: newKey }))
                    : draft.chatSections
            };
            conversationDraftsRef.current.set(newKey, migratedDraft);
            conversationDraftsRef.current.delete(oldKey);
        }
        // Move the live request handles to the new key so stop/cleanup still find them.
        for (const map of [streamControllersRef.current, streamEsRef.current, streamRequestIdRef.current]) {
            if (map.has(oldKey)) {
                map.set(newKey, map.get(oldKey));
                map.delete(oldKey);
            }
        }
        if (activeConversationKeyRef.current === oldKey) {
            activeConversationKeyRef.current = newKey;
        }
        if (activeStreamConversationKeyRef.current === oldKey) {
            activeStreamConversationKeyRef.current = newKey;
        }
    }

    // Mirror a stream's loading state to the on-screen UI ONLY when that stream's
    // conversation is the one currently being viewed. Background streams (the user
    // switched away, or another browser tab) keep updating their own draft but never
    // flip the spinner/progress bar on the conversation in front of you.
    // Same contract as setMessagesForConversation: always persist; mirror if active.
    function setLoadingForConversation(conversationKey, { isLoading, isProcessing } = {}) {
        const patch = {};
        if (typeof isLoading === 'boolean') patch.isLoading = isLoading;
        if (typeof isProcessing === 'boolean') patch.isProcessing = isProcessing;
        if (conversationKey) saveConversationDraft(conversationKey, patch);
        if (!conversationKey || conversationKey === activeConversationKeyRef.current) {
            if (typeof isLoading === 'boolean') {
                setIsLoading(isLoading);
                setGlobalRequestRunning(isLoading);
            }
            if (typeof isProcessing === 'boolean') setIsProcessing(isProcessing);
        }
    }

    function setMessagesForConversation(conversationKey, updater) {
        const active = activeConversationKeyRef.current === conversationKey;
        const base = active
            ? messagesRef.current
            : (conversationDraftsRef.current.get(conversationKey)?.messages || []);
        const next = typeof updater === 'function' ? updater(base) : updater;
        saveConversationDraft(conversationKey, { messages: next });
        if (active) setMessages(next);
        return next;
    }

    function setChatSectionsForConversation(conversationKey, updater) {
        const active = activeConversationKeyRef.current === conversationKey;
        const base = active
            ? chatSectionsRef.current
            : (conversationDraftsRef.current.get(conversationKey)?.chatSections || []);
        const rawNext = typeof updater === 'function' ? updater(base) : updater;
        const next = Array.isArray(rawNext)
            ? rawNext.map(section => ({ ...section, conversationKey }))
            : rawNext;
        saveConversationDraft(conversationKey, { chatSections: next });
        if (active) setChatSections(next);
        return next;
    }

    function isSectionForActiveConversation(section) {
        return !section?.conversationKey || section.conversationKey === activeConversationKeyRef.current;
    }

    function isLiveSectionForActiveConversation(section) {
        return Boolean(section?.conversationKey && section.conversationKey === activeConversationKeyRef.current);
    }

    const hasActiveSubscriptionAccess = (subscription) => {
        if (!subscription?.status) return false;
        if (subscription.status === 'active') return true;
        if (subscription.status === 'cancelled' && subscription.planEndDate) {
            return new Date(subscription.planEndDate) > new Date();
        }
        return false;
    };

    const showRequestGuardMessage = useCallback(() => {
        setRequestGuardMessage('One request is already running. Return after that request is complete.');
        if (requestGuardTimerRef.current) {
            clearTimeout(requestGuardTimerRef.current);
        }
        requestGuardTimerRef.current = setTimeout(() => {
            setRequestGuardMessage('');
            requestGuardTimerRef.current = null;
        }, 3500);
    }, []);

    const getUserFacingErrorMessage = (error) => {
        if (error?.message === 'FREE_TIER_EXHAUSTED') {
            return 'Your Free Tier is exhausted.';
        }
        if (error?.code === 'OPENAI_EMBEDDING_QUOTA_EXHAUSTED' || error?.message?.includes('OpenAI embedding quota is exhausted')) {
            return 'Search is temporarily unavailable because the embedding service quota is exhausted.';
        }
        return 'Something went wrong. Please try again.';
    };

    // Custom ReactMarkdown components configuration
    const markdownComponents = {
        p: ({ children }) => (
            <p style={{
                color: 'rgb(75, 85, 99)',
                marginBottom: '1rem',
                lineHeight: '1.625'
            }}>{children}</p>
        ),
        h1: ({ children }) => (
            <h1 style={{
                color: 'rgb(75, 85, 99)',
                marginTop: '1.5rem',
                marginBottom: '0.75rem',
                fontSize: '2rem',
                fontWeight: 'bold'
            }}>{children}</h1>
        ),
        h2: ({ children }) => (
            <h2 style={{
                color: 'rgb(75, 85, 99)',
                marginTop: '1.25rem',
                marginBottom: '0.625rem',
                fontSize: '1.5rem',
                fontWeight: 'bold'
            }}>{children}</h2>
        ),
        h3: ({ children }) => (
            <h3 style={{
                color: 'rgb(75, 85, 99)',
                marginTop: '1rem',
                marginBottom: '0.5rem',
                fontSize: '1.25rem',
                fontWeight: 'bold'
            }}>{children}</h3>
        ),
        ul: ({ children }) => (
            <ul style={{
                color: 'rgb(75, 85, 99)',
                marginBottom: '1rem',
                paddingLeft: '1.5rem',
                listStyleType: 'disc'
            }}>{children}</ul>
        ),
        li: ({ children }) => (
            <li style={{
                color: 'rgb(75, 85, 99)',
                marginBottom: '0.5rem',
                lineHeight: '1.625'
            }}>{children}</li>
        ),
        strong: ({ children }) => (
            <strong style={{
                color: 'rgb(75, 85, 99)',
                fontWeight: '600'
            }}>{children}</strong>
        ),
        hr: () => (
            <hr style={{
                borderColor: 'rgb(209, 213, 219)',
                marginTop: '1.5rem',
                marginBottom: '1.5rem'
            }} />
        ),
    };

    // NEW: Anthropic-style SSE Streaming Handler
    const streamChatSSEAnthropicStyle = async (
        requestData,
        onDelta,             // (text) => void callback for each text chunk
        signal,
        setThinkingobj,
        setChatSections,
        savedInput,
        controller,
        requestId,
        first_img,
        additionalParams,
        streamScope          // { key } — which conversation this stream belongs to
    ) => {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        
        // Attach Firebase token & uid
        let firebaseToken = null;
        let firebaseId = null;
        
        try {
            const { auth } = await import('../firebase/firebaseConfig');
            const user = auth.currentUser;
            if (user) {
                firebaseToken = await user.getIdToken();
                firebaseId = user.uid;
                requestData.authToken = firebaseToken;
                requestData.firebaseId = firebaseId;

                const statusResponse = await fetch(`${API_BASE_URL}/api/auth/user-status`, {
                    headers: {
                        Authorization: `Bearer ${firebaseToken}`
                    }
                });

                if (statusResponse.ok) {
                    const status = await statusResponse.json();
                    const freeRequestsRemaining = Number(status.freeRequestsRemaining || 0);
                    if (freeRequestsRemaining <= 0 && !hasActiveSubscriptionAccess(status.subscription)) {
                        console.warn('[LANDINGPAGE] Free tier exhausted for user:', firebaseId);
                        additionalParams?.setUnpaid?.(true);
                        throw new Error('FREE_TIER_EXHAUSTED');
                    }
                    additionalParams?.setUnpaid?.(false);
                } else {
                    console.error('[LANDINGPAGE] Failed to check user status before SSE:', statusResponse.status);
                }
            }
        } catch (error) {
            if (error.message === 'FREE_TIER_EXHAUSTED') {
                throw error;
            }
            console.error('Could not get auth token:', error);
        }

        // Store the large payload server-side first. EventSource is GET-only, and long
        // legal job descriptions can exceed production URL/proxy limits if placed in
        // the query string.
        let sseRequestId = requestId || requestData.requestId;
        try {
            const startHeaders = { 'Content-Type': 'application/json' };
            if (firebaseToken) startHeaders.Authorization = `Bearer ${firebaseToken}`;
            const startResponse = await fetch(`${API_BASE_URL}/api/chat-sse/start`, {
                method: 'POST',
                headers: startHeaders,
                body: JSON.stringify(requestData)
            });
            if (!startResponse.ok) {
                const text = await startResponse.text();
                throw new Error(`SSE start failed: ${startResponse.status} ${text}`);
            }
            const startData = await startResponse.json();
            sseRequestId = startData.requestId || sseRequestId;
        } catch (error) {
            console.error('[SSE] Failed to start streaming session:', error);
            throw error;
        }

        // Build short URL with query params
        let url = `${API_BASE_URL}/api/chat-sse?requestId=${encodeURIComponent(sseRequestId)}`;
        if (firebaseToken) url += `&token=${encodeURIComponent(firebaseToken)}`;
        if (firebaseId) url += `&firebaseId=${encodeURIComponent(firebaseId)}`;

        return new Promise((resolve, reject) => {
            // Create EventSource connection
            const es = new EventSource(url, { withCredentials: false });
            esRef.current = es; // shared "latest" pointer (kept for back-compat)
            if (streamScope?.key) streamEsRef.current.set(streamScope.key, es); // per-conversation handle for scoped stop

            let accumulated = '';
            let messageStarted = false;
            let shortlistCandidates = null; // exact candidates the report covered (from the 'shortlist' event)

            // Helper to update state with accumulated text
            const updateProgress = (loading, stage) => {
                setThinkingobj(prev => {
                    const extra = shortlistCandidates ? { shortlistCandidates } : {};
                    const newObj = {
                        ...prev,
                        iterations: {
                            ...prev.iterations,
                            0: {
                                ...(prev.iterations?.[0] || {}),
                                subtopics: 'dummy',
                                initialAnalysis: accumulated, // Raw markdown text
                                isHTML: false,
                                isMarkdown: true,
                                timestamp: new Date().toISOString()
                            }
                        },
                        loading,
                        stage,
                        ...extra
                    };

                    setChatSections(prevChats =>
                        patchOrAppendChatSections(prevChats, newObj, {
                            iterations: { ...newObj.iterations },
                            loading,
                            stage,
                            ...extra
                        })
                    );

                    return newObj;
                });
            };

            // Handle message_start event
            es.addEventListener('message_start', (event) => {
                console.log('[SSE] Message started');
                messageStarted = true;
                updateProgress(true, 'Generating Initial Report');
            });

            // Handle content_block_start event
            es.addEventListener('content_block_start', (event) => {
                console.log('[SSE] Content block started');
            });

            // Handle delta events (main text streaming)
            es.addEventListener('delta', (event) => {
                try {
                    const data = JSON.parse(event.data);
                    const text = data.text || '';
                    
                    if (text) {
                        accumulated += text;
                        
                        // Call the optional onDelta callback
                        if (onDelta) {
                            onDelta(text);
                        }
                        
                        // Update state with accumulated text
                        updateProgress(true, 'Generating Initial Report');
                    }
                } catch (error) {
                    console.error('[SSE] Error parsing delta:', error);
                }
            });

            // Handle content_block_stop event
            es.addEventListener('content_block_stop', (event) => {
                console.log('[SSE] Content block stopped');
            });

            // Exact shortlist the report covered (with emails). Stored on the section so the
            // inline outreach panel uses these candidates instead of re-running its own search.
            es.addEventListener('shortlist', (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (Array.isArray(data?.candidates)) {
                        shortlistCandidates = data.candidates;
                        updateProgress(true, 'Generating Initial Report'); // flush into the section
                    }
                } catch (error) {
                    console.error('[SSE] Error parsing shortlist:', error);
                }
            });

            // Handle message_delta event (usage info)
            es.addEventListener('message_delta', (event) => {
                try {
                    const data = JSON.parse(event.data);
                    console.log('[SSE] Usage:', data.usage);
                } catch (error) {
                    console.error('[SSE] Error parsing message_delta:', error);
                }
            });

            // Handle message_stop event
            es.addEventListener('message_stop', async (event) => {
                console.log('[SSE] Message stopped');
                
                // Close the EventSource
                es.close();
                if (esRef.current === es) esRef.current = null;

                // Update state to show completion
                updateProgress(false, 'Initial Report Completed');
                
                // Trigger mainWorkflow with the accumulated text
                // try {
                //     const workflowPromise = mainWorkflow(
                //         additionalParams.Searchweb,
                //         additionalParams.setLoadingarticles,
                //         additionalParams.setErrorarticles,
                //         additionalParams.DeepSearch,
                //         additionalParams.Think,
                //         savedInput,
                //         first_img,
                //         setThinkingobj,
                //         additionalParams.setSubtopicshook,
                //         additionalParams.articleshook,
                //         additionalParams.setArticleshook,
                //         additionalParams.setFinalSummaryhook,
                //         additionalParams.setIter1Hook,
                //         additionalParams.chatSections,
                //         setChatSections,
                //         additionalParams.thinkingobj,
                //         controller.signal,
                //         additionalParams.setIsLoading,
                //         additionalParams.apiError,
                //         additionalParams.setAPIError,
                //         additionalParams.Actor_Model_apiProvider,
                //         additionalParams.setActor_Model_apiProvider,
                //         additionalParams.Critic_Model_apiProvider,
                //         additionalParams.setCritic_Model_apiProvider,
                //         additionalParams.Actor_Model_apiModel,
                //         additionalParams.setActor_Model_apiModel,
                //         additionalParams.Critic_Model_apiModel,
                //         additionalParams.setCritic_Model_apiModel,
                //         additionalParams.AI_Agent_Model_apikey,
                //         () => {},
                //         additionalParams.AI_Agent_Model_apikey,
                //         () => {},
                //         additionalParams.temperature_val_Actor_Model_apiProvider,
                //         additionalParams.setTemperature_val_Actor_Model_apiProvider,
                //         additionalParams.temperature_val_Critic_Model_apiProvider,
                //         additionalParams.setTemperature_val_Critic_Model_apiProvider,
                //         additionalParams.profileCountChange,
                //         requestId,
                //         additionalParams.setUnpaid,
                //         accumulated // Pass raw text forward
                //     );

                //     await workflowPromise;
                //     console.log('[SSE] Workflow completed successfully');
                // } catch (error) {
                //     if (
                //         error.name === 'AbortError' ||
                //         error.message === 'ABORT_SIGNAL' ||
                //         error.message?.includes('aborted') ||
                //         error.message?.includes('cancelled')
                //     ) {
                //         console.log('[SSE] Operation cancelled by user');
                //     } else {
                //         console.error('[SSE] Workflow error:', error);
                //         throw error;
                //     }
                // }
                
                resolve(accumulated);
            });

            // Handle ping events (keepalive)
            es.addEventListener('ping', (event) => {
                // No-op, just keepalive
            });

            // Handle error events
            es.addEventListener('error', (event) => {
                console.error('[SSE] Error event:', event);

                const eventMessage = event?.data ? (() => {
                    try {
                        return JSON.parse(event.data);
                    } catch {
                        return { message: event.data };
                    }
                })() : null;

                if (eventMessage?.type === 'abort') {
                    console.log('[SSE] Backend reported request abort');
                    es.close();
                    if (esRef.current === es) esRef.current = null;
                    reject(new Error('REQUEST_ABORTED'));
                    return;
                }

                if (eventMessage?.message) {
                    es.close();
                    if (esRef.current === es) esRef.current = null;
                    const backendError = new Error(eventMessage.message);
                    if (eventMessage.code) backendError.code = eventMessage.code;
                    reject(backendError);
                    return;
                }
                
                // Check if it's because of abort
                if (signal?.aborted) {
                    console.log('[SSE] Connection aborted by user');
                    es.close();
                    if (esRef.current === es) esRef.current = null;
                    reject(new Error('REQUEST_ABORTED'));
                    return;
                }
                
                // Close and reject on error
                es.close();
                if (esRef.current === es) esRef.current = null;
                reject(new Error('SSE connection failed before completion. Check backend logs for the underlying error.'));
            });

            // Handle abort signal
            if (signal) {
                signal.addEventListener('abort', () => {
                    console.log('[SSE] Aborting EventSource connection');
                    if (es.readyState !== EventSource.CLOSED) {
                        es.close();
                        if (esRef.current === es) esRef.current = null;
                    }
                });
            }
        });
    };

    // Detect mobile viewport
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Mobile-responsive styles
    const mobileStyles = {
        mainContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'start',
            opacity: 1,
            marginTop: isMobile ? '15%' : '6%',
            padding: isMobile ? '0 10px' : '0'
        },
        heroContainer: {
            textAlign: 'center',
            marginTop: isMobile ? '0.5%' : '1%',
            padding: isMobile ? '0 15px' : '0'
        },
        heroImage: {
            height: isMobile ? '48px' : '43px',
            marginLeft: '0px',
            borderRadius: '15px'
        },
        heroBadge: {
            fontFamily: 'sans-serif',
            display: 'inline-block',
            padding: isMobile ? '0.5rem 0.75rem' : '0.675rem 1rem',
            backgroundColor: 'rgb(17 24, 39)',
            color: 'white',
            borderRadius: '9999px',
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            fontWeight: 600,
            marginBottom: '-0.2em'
        },
        heroTitle: {
            fontSize: isMobile ? '1.75rem' : '3rem',
            fontFamily: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
            color: 'rgb(17 24, 39)',
            marginBottom: isMobile ? '0.5rem' : '1rem',
            letterSpacing: '-0.025em',
            fontWeight: 200,
            width: isMobile ? '95%' : '78%',
            marginLeft: 'auto',
            marginRight: 'auto',
            lineHeight: isMobile ? '1.3' : '1.2'
        },
        heroSubtitle: {
            fontFamily: 'sans-serif',
            fontSize: isMobile ? '1rem' : '1.25rem',
            color: 'rgb(75 85, 99 / 58%)',
            fontWeight: 100,
            maxWidth: isMobile ? '100%' : '42rem',
            marginLeft: 'auto',
            marginRight: 'auto',
            lineHeight: 1.625,
            padding: isMobile ? '0 15px' : '0'
        },
        formContainer: {
            position: hasSubmitted1 ? 'fixed' : 'relative',
            height: hasSubmitted1 ? '19vh' : '',
            bottom: hasSubmitted1 ? '20px' : 'auto',
            left: hasSubmitted1 ? '50%' : 'auto',
            transform: hasSubmitted1 ? 'translateX(-50%)' : 'none',
            zIndex: hasSubmitted1 ? 4 : 0,
            width: isMobile ? '90vw' : '47.5vw',
            margin: hasSubmitted1 ? '0' : '0 auto',
            display: 'flex',
            justifyContent: 'center'
        },
        formInput: {
            width: '100%',
            position: 'relative',
            backgroundColor: 'rgb(249 250 251)',
            borderRadius: isMobile ? '20px' : '25px',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            border: '1px solid rgb(209 213 219)',
            paddingBottom: isMobile ? '48px' : '52px'
        },
        textarea: {
            width: isMobile ? '75%' : '82%',
            fontSize: isMobile ? 'medium' : 'large',
            padding: isMobile ? '1rem' : '1.25rem',
            paddingRight: isMobile ? '4rem' : '8rem',
            borderRadius: isMobile ? '20px' : '25px',
            border: '2px solid transparent',
            backgroundColor: 'transparent',
            resize: 'none',
            height: 'auto',
            minHeight: input.length === 0 ? (isMobile ? '45px' : '60px') : (isMobile ? '60px' : '92px'),
            maxHeight: isMobile ? '120px' : '200px',
            outline: 'none',
            overflow: 'auto',
            transition: 'min-height 0.2s ease'
        },
        profileButton: {
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            padding: isMobile ? '3px 4px' : '5px 6px',
            backgroundColor: 'white',
            borderRadius: '50px',
            border: '1px solid #9e9e9e29',
            fontSize: isMobile ? '0.75rem' : '1rem'
        },
        timelineContainer: {
            color: "white",
            backgroundColor: "#121212",
            width: "100%",
            minHeight: isMobile ? "24vh" : "32vh",
            marginBottom: "0vh",
            marginTop: isMobile ? "-3vh" : "-5vh",
            position: "relative",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 0 15px rgba(0,0,0,0.5)",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        },
        timelineText: {
            fontSize: isMobile ? "11px" : "13px",
            color: "white",
            backgroundColor: "rgba(170, 170, 170, 0.1)",
            borderLeft: "2px solid #aaaaaa",
            padding: isMobile ? "6px 8px" : "8px 12px",
            borderRadius: "4px"
        },
        chatContainer: {
            display: 'block',
            width: '100%',
            maxWidth: isMobile ? '95vw' : '80vw',
            marginLeft: isMobile ? '0.5rem' : '1rem',
            marginRight: isMobile ? '0.5rem' : '1rem',
            marginBottom: hasSubmitted1 ? (isMobile ? '12rem' : '19rem') : '8rem',
            marginTop: hasSubmitted1 ? (isMobile ? '3rem' : '4.5rem') : '10rem'
        },
        messageBubble: {
            maxWidth: isMobile ? '95%' : '90%',
            backgroundColor: 'rgba(75,85,99,0.1)',
            borderRadius: '0.75rem',
            padding: isMobile ? '0.75rem' : '1rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            borderTopLeftRadius: '0.25rem',
            marginLeft: '0',
            marginRight: 'auto',
            lineHeight: isMobile ? '22px' : '25.5px',
            fontSize: isMobile ? '14px' : '16.5px'
        },
        heading: {
            margin: '0 0 0.25rem',
            fontSize: isMobile ? '16px' : '19px',
            fontWeight: 600,
            color: 'rgb(75,85,99)'
        },
        textContent: {
            margin: '0.5rem 0',
            fontSize: isMobile ? '14px' : '16.5px',
            color: 'rgb(75,85,99)',
            lineHeight: isMobile ? '1.5' : '1.625'
        },
        avatarLogo: {
            width: isMobile ? '32px' : '45px',
            height: isMobile ? '32px' : '45px',
            marginLeft: isMobile ? '-60px' : '-80px',
            borderRadius: '15px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            border: '1px solid rgba(75,85,99,0.2)',
            backgroundColor: 'rgb(158 158 158 / 20%)',
            position: 'absolute'
        },
        fabButton: {
            position: "fixed",
            right: isMobile ? "5vw" : "10vw",
            bottom: isMobile ? "10vh" : "10vh",
            zIndex: 1000,
            transform: isMobile ? 'scale(0.8)' : 'scale(1)'
        }
    };

    // Animation keyframes setup
    useEffect(() => {
        const handleUnhandledRejection = (event) => {
            if (event.reason && (
                event.reason.name === 'AbortError' ||
                event.reason.message?.includes('aborted') ||
                event.reason.message?.includes('cancelled') ||
                event.reason.message?.includes('BodyStreamBuffer')
            )) {
                event.preventDefault();
            }
        };

        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        const styleSheet = document.createElement("style");
        styleSheet.id = "processing-animation-style";
        styleSheet.type = "text/css";
        styleSheet.innerText = `
            @keyframes pulseScale {
                0% { transform: scale(1); }
                50% { transform: scale(1.05); }
                100% { transform: scale(1); }
            }
            .processing-text-animation {
                display: inline-block;
                animation: pulseScale 1.5s infinite ease-in-out;
            }
            @keyframes slideInFromLeft {
                0% { 
                    transform: translateX(-10%);
                    opacity: 0;
                }
                100% { 
                    transform: translateX(0);
                    opacity: 1;
                }
            }
            @keyframes slideInFromRight {
                0% { 
                    transform: translateX(+100%);
                    opacity: 0;
                }
                100% { 
                    transform: translateX(0);
                    opacity: 1;
                }
            }
            @keyframes fadeIn {
                from {
                    opacity: 0;
                }
                to {
                    opacity: 1;
                }
            }
            @keyframes progress {
                0% {
                    transform: translateX(-100%);
                }
                50% {
                    transform: translateX(0%);
                }
                100% {
                    transform: translateX(100%);
                }
            }
            @keyframes pulse {
                0% {
                    transform: scale(1);
                    opacity: 1;
                }
                50% {
                    transform: scale(1.1);
                    opacity: 0.8;
                }
                100% {
                    transform: scale(1);
                    opacity: 1;
                }
            }
            @keyframes slideInFromTop {
                from {
                    transform: translateX(-50%) translateY(-10px);
                    opacity: 0;
                }
                to {
                    transform: translateX(-50%) translateY(0);
                    opacity: 1;
                }
            }
            .markdown-content {
                color: rgb(75, 85, 99);
                line-height: 1.625;
            }
            .markdown-content h1,
            .markdown-content h2,
            .markdown-content h3 {
                color: rgb(75, 85, 99);
                margin-top: 1.25rem;
                margin-bottom: 0.75rem;
                font-weight: bold;
            }
            .markdown-content h1 { font-size: 2rem; }
            .markdown-content h2 { font-size: 1.5rem; }
            .markdown-content h3 { font-size: 1.25rem; }
            .markdown-content p {
                margin-bottom: 1rem;
                line-height: 1.625;
            }
            .markdown-content ul {
                margin-bottom: 1rem;
                padding-left: 1.5rem;
                list-style-type: disc;
            }
            .markdown-content li {
                margin-bottom: 0.5rem;
                line-height: 1.625;
            }
            .markdown-content strong {
                font-weight: 600;
            }
            .markdown-content hr {
                border-color: rgb(209, 213, 219);
                margin-top: 1.5rem;
                margin-bottom: 1.5rem;
            }
        `;
        if (!document.getElementById("processing-animation-style")) {
            document.head.appendChild(styleSheet);
        }

        return () => {
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
            const existingStyleSheet = document.getElementById("processing-animation-style");
            if (existingStyleSheet) {
                document.head.removeChild(existingStyleSheet);
            }
        };
    }, []);

    useEffect(() => {
        let timer;
        if (stopButton) {
            timer = setTimeout(() => setStopButton(false), 2000);
        }
        return () => clearTimeout(timer);
    }, [stopButton]);

    // Storage change listener
    useEffect(() => {
        const handleStorageChange = (event) => {
            if (event.key === 'isChecked') {
                try {
                    const newValue = event.newValue !== null ? JSON.parse(event.newValue) : false;
                    setIsChecked(newValue);
                } catch (e) {
                    console.error('Error parsing isChecked from storage event:', e);
                    setIsChecked(false);
                }
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    let first_model = Actor_Model_apiProvider;
    useEffect(() => {
        if (first_model === 'Deepseek') {
            setFirst_img('Deepseek');
        } else if (first_model === 'Google Gemini') {
            setFirst_img('Google Gemini');
        } else if (first_model === 'Anthropic') {
            setFirst_img('Anthropic');
        }
    }, [first_model]);

    let second_model = Critic_Model_apiProvider;
    useEffect(() => {
        if (second_model === 'Deepseek') {
            setSecond_img('Deepseek');
        } else if (second_model === 'Google Gemini') {
            setSecond_img('Google Gemini');
        } else if (second_model === 'Anthropic') {
            setSecond_img('Anthropic');
        }
    }, [second_model]);

    const scrollToBottom = () => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (bottomRef.current) {
            const scrollToElement = (element) => {
                const startPosition = window.pageYOffset || document.documentElement.scrollTop;
                const targetPosition = element.getBoundingClientRect().top + startPosition;
                const distance = targetPosition - startPosition;
                const duration = 1500;
                let startTime = null;

                function animation(currentTime) {
                    if (startTime === null) startTime = currentTime;
                    const timeElapsed = currentTime - startTime;
                    const progress = Math.min(timeElapsed / duration, 1);

                    const easeProgress = progress < 0.5
                        ? 2 * progress * progress
                        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

                    if (timeElapsed < duration) {
                        requestAnimationFrame(animation);
                    }
                }

                requestAnimationFrame(animation);
            };

            scrollToElement(bottomRef.current);
        }
    }, [chatSections]);

    useEffect(() => {
        console.log('[LANDINGPAGE] chatSections updated:', {
            length: chatSections.length,
            lastSection: chatSections[chatSections.length - 1]
        });

        if (chatSections.length > 0) {
            const lastSection = chatSections[chatSections.length - 1];
            if (lastSection?.iterations) {
                console.log('[LANDINGPAGE] Current iterations state:', lastSection.iterations);
            }
        }
    }, [chatSections]);

    // Load saved configuration
    useEffect(() => {
        const loadSavedConfig = () => {
            const actorProvider = localStorage.getItem('Actor Model-apiProvider');
            const actorModel = localStorage.getItem('Actor Model-apiModel');
            const actorKey = localStorage.getItem('Actor Model-apikey');
            const actorTemp = localStorage.getItem('Actor Model-temperature_val');

            const criticProvider = localStorage.getItem('Critic Model-apiProvider');
            const criticModel = localStorage.getItem('Critic Model-apiModel');
            const criticKey = localStorage.getItem('Critic Model-apikey');
            const criticTemp = localStorage.getItem('Critic Model-temperature_val');

            if (actorProvider) {
                setActor_Model_apiProvider(actorProvider);
            }
            if (actorModel) {
                setActor_Model_apiModel(actorModel);
            }
            if (actorTemp) {
                setTemperature_val_Actor_Model_apiProvider(actorTemp);
            }

            if (criticProvider) {
                setCritic_Model_apiProvider(criticProvider);
            }
            if (criticModel) {
                setCritic_Model_apiModel(criticModel);
            }
            if (criticTemp) {
                setTemperature_val_Critic_Model_apiProvider(criticTemp);
            }
        };

        loadSavedConfig();
    }, []);

    // MODIFIED handleSubmit1 to use SSE streaming
    const handleSubmit1 = async (e) => {
        e.preventDefault();
        console.log('workflow1 stage1')

        // Claude-style concurrency: ONE active request per conversation/tab, but
        // different conversations/tabs run independently. Block a second submit only
        // for the conversation currently on screen if it is already running; other
        // conversations (and other browser tabs) are unaffected.
        const viewingKey = activeConversationKeyRef.current;
        if (viewingKey && conversationDraftsRef.current.get(viewingKey)?.isLoading) {
            console.log('[LANDINGPAGE] This conversation already has a running request; stop it or wait.');
            return;
        }

        const requestId = uuidv4();
        const pendingConversationKey = `pending:${requestId}`;
        const streamScope = { key: pendingConversationKey };
        activeConversationKeyRef.current = pendingConversationKey;
        activeStreamConversationKeyRef.current = pendingConversationKey;
        activeRequestRef.current = requestId;
        setGlobalRequestRunning(true);
        setRequestGuardMessage('');
        setCurrentRequestId(requestId);
        console.log(`[LANDINGPAGE] Starting new request with ID: ${requestId}`);

        // Do NOT abort any in-flight request here: a previously started conversation
        // may still be streaming in the background and must keep running. Each request
        // owns its own AbortController, tracked per conversation so stop is scoped.
        const controller = new AbortController();
        abortControllerRef.current = controller; // shared "latest" (back-compat)
        streamControllersRef.current.set(streamScope.key, controller);
        streamRequestIdRef.current.set(streamScope.key, requestId);

        setAPIError(false);
        setLoadingForConversation(streamScope.key, { isLoading: true, isProcessing: true });
        setStopButton(false);
        setError(null);
        setSearchStatus('');

        setThinkingobj((prev) => {
            newSectionFlagRef.current = true;
            console.log('[LANDINGPAGE] Resetting thinkingobj for new request');
            return {};
        });

        setHasSubmitted1(true);

        setThinkingobj((prevState) => {
            const newObj = {
                ...prevState,
                query: input,
                requestId: requestId,
                loading: true,
                error: false,
                stopped: false,
                stage: 'Initializing'
            };
            setChatSectionsForConversation(streamScope.key, (prevChats) => {
                return patchOrAppendChatSections(prevChats, newObj);
            });
            saveConversationDraft(streamScope.key, { thinkingObj: newObj, isLoading: true, isProcessing: true });
            return newObj;
        });

        const userMessage = { role: 'user', content: input };
        setMessagesForConversation(streamScope.key, (prev) => [...prev, userMessage]);

        const savedInput = input;
        setInput('');

        // Persist to chat history → creates the sidebar entry (title = this query).
        // Fire-and-forget: never block or break the chat flow.
        const conversationIdPromise = (() => {
            try {
                return Promise.resolve(conversation?.handleUserQuery(savedInput));
            } catch (e) {
                return Promise.resolve(null);
            }
        })();

        // Make this search available to the Outreach pipeline (search → outreach handoff).
        try { localStorage.setItem('outreachRoleQuery', savedInput); } catch (e) { /* noop */ }

        try {
            console.log(`[LANDINGPAGE] Processing query: "${savedInput}"`);
            console.log(`[LANDINGPAGE] Configuration:
            - Deep Search: ${DeepSearch}
            - Think: ${Think}
            - Search Web: ${Searchweb}
            - Profile Count: ${profileCountChange}
            - Actor Model: ${Actor_Model_apiProvider}
            - Critic Model: ${Critic_Model_apiProvider}
        `);

            const requestData = {
                messages: [{ role: 'user', content: savedInput }],
                profileCountChange: profileCountChange,
                requestId: requestId,
                loopCount: parseInt(localStorage.getItem('loops') || '0'),
                actorModel: {
                    provider: Actor_Model_apiProvider,
                    model: Actor_Model_apiModel,
                    temperature: parseFloat(temperature_val_Actor_Model_apiProvider) || 0.7
                },
                criticModel: {
                    provider: Critic_Model_apiProvider,
                    model: Critic_Model_apiModel,
                    temperature: parseFloat(temperature_val_Critic_Model_apiProvider) || 0.7
                }
            };

            const additionalParams = {
                Searchweb,
                setLoadingarticles,
                setErrorarticles,
                DeepSearch,
                Think,
                setSubtopicshook,
                articleshook,
                setArticleshook,
                setFinalSummaryhook,
                setIter1Hook,
                chatSections,
                thinkingobj,
                setIsLoading,
                apiError,
                setAPIError,
                Actor_Model_apiProvider,
                setActor_Model_apiProvider,
                Critic_Model_apiProvider,
                setCritic_Model_apiProvider,
                Actor_Model_apiModel,
                setActor_Model_apiModel,
                Critic_Model_apiModel,
                setCritic_Model_apiModel,
                AI_Agent_Model_apikey,
                temperature_val_Actor_Model_apiProvider,
                setTemperature_val_Actor_Model_apiProvider,
                temperature_val_Critic_Model_apiProvider,
                setTemperature_val_Critic_Model_apiProvider,
                profileCountChange,
                setUnpaid
            };

            const resolvedConversationId = await conversationIdPromise;
            if (resolvedConversationId) {
                migrateConversationDraft(streamScope.key, resolvedConversationId);
                streamScope.key = resolvedConversationId;
                activeStreamConversationKeyRef.current = resolvedConversationId;
            }

            const scopedSetChatSections = (updater) => setChatSectionsForConversation(streamScope.key, updater);

            // Use SSE streaming instead of multipart/mixed
            const assistantText = await streamChatSSEAnthropicStyle(
                requestData,
                (delta) => {
                    // Optional: log each chunk received
                    console.log(`[SSE Delta] Received ${delta.length} chars`);
                },
                controller.signal,
                setThinkingobj,
                scopedSetChatSections,
                savedInput,
                controller,
                requestId,
                first_img,
                additionalParams,
                streamScope
            );

            console.log('[LANDINGPAGE] SSE streaming complete');
            console.log('workflow1 stage2');

            // Persist assistant response + a snapshot of the rendered sections (for replay).
            try {
                const finalDraft = conversationDraftsRef.current.get(streamScope.key);
                conversation?.handleAssistantResponse(
                    typeof assistantText === 'string' ? assistantText : '',
                    { chatSections: finalDraft?.chatSections || chatSectionsRef.current },
                    streamScope.key?.startsWith('pending:') ? null : streamScope.key
                );
            } catch (e) { /* noop */ }

            setThinkingobj((prevState) => {
                const newObj = {
                    ...prevState,
                    loading: false,
                    stage: 'Initial Report Completed'
                };
                setChatSectionsForConversation(streamScope.key, (prev) => {
                    return patchOrAppendChatSections(prev, newObj, {
                        loading: false,
                        stage: 'Initial Report Completed'
                    });
                });
                saveConversationDraft(streamScope.key, { thinkingObj: newObj, isLoading: false });
                return newObj;
            });

            setLoadingForConversation(streamScope.key, { isLoading: true, isProcessing: true });

            console.log('workflow1 stage3');
            console.log(`[LANDINGPAGE] Request ${requestId} completed successfully`);

            setThinkingobj((prevState) => {
                const newObj = {
                    ...prevState,
                    stage: 'Completed',
                    loading: false,
                    error: false,
                    completedAt: new Date().toISOString()
                };
                setChatSectionsForConversation(streamScope.key, (prev) => {
                    return patchOrAppendChatSections(prev, newObj, {
                        loading: false,
                        error: false
                    });
                });
                saveConversationDraft(streamScope.key, { thinkingObj: newObj, isLoading: false, isProcessing: false });
                return newObj;
            });

        } catch (error) {
            console.error(`[LANDINGPAGE] Error in handleSubmit1 for request ${requestId}:`, error);

            if (error.message === 'FREE_TIER_EXHAUSTED') {
                setUnpaid(true);
                setLoadingForConversation(streamScope.key, { isLoading: false, isProcessing: false });

                setThinkingobj((prevState) => {
                    const newObj = {
                        ...prevState,
                        error: false,
                        loading: false,
                        stopped: false,
                        stage: 'Free Tier Exhausted'
                    };
                    setChatSectionsForConversation(streamScope.key, (prev) => {
                        return patchOrAppendChatSections(prev, newObj, {
                            error: false,
                            loading: false,
                            stopped: false,
                            stage: 'Free Tier Exhausted'
                        });
                    });
                    saveConversationDraft(streamScope.key, { thinkingObj: newObj, isLoading: false, isProcessing: false });
                    return newObj;
                });

                return;
            }

            if (error.name === 'AbortError' ||
                error.message === 'ABORT_SIGNAL' ||
                error.message === 'REQUEST_ABORTED' ||
                error.message?.includes('cancelled') ||
                error.message?.includes('BodyStreamBuffer')) {

                console.log(`[LANDINGPAGE] Request ${requestId} was aborted by user`);
                setLoadingForConversation(streamScope.key, { isLoading: false, isProcessing: false });

                setThinkingobj((prevState) => {
                    const newObj = {
                        ...prevState,
                        error: true,
                        loading: false,
                        stopped: true,
                        stoppedAt: new Date().toISOString(),
                        stoppedMessage: 'Analysis stopped by user',
                        stage: 'Stopped'
                    };
                    setChatSectionsForConversation(streamScope.key, (prev) => {
                        return patchOrAppendChatSections(prev, newObj, {
                            error: true,
                            loading: false,
                            stopped: true,
                            stoppedMessage: 'Analysis stopped by user',
                            stage: 'Stopped'
                        });
                    });
                    saveConversationDraft(streamScope.key, { thinkingObj: newObj, isLoading: false, isProcessing: false });
                    return newObj;
                });

            } else {
                console.error('Unhandled error in mainWorkflow:', error);
                console.error('Error stack:', error.stack);

                setThinkingobj((prevState) => {
                    const newObj = {
                        ...prevState,
                        error: true,
                        loading: false,
                        errorMessage: getUserFacingErrorMessage(error),
                        stage: 'Error',
                        timout_err: error.message?.includes('timeout') || error.message?.includes('Timeout')
                    };
                    setChatSectionsForConversation(streamScope.key, (prev) => {
                        return patchOrAppendChatSections(prev, newObj, {
                            error: true,
                            loading: false,
                            errorMessage: getUserFacingErrorMessage(error),
                            stage: 'Error',
                            timout_err: error.message?.includes('timeout') || error.message?.includes('Timeout')
                        });
                    });
                    saveConversationDraft(streamScope.key, { thinkingObj: newObj, isLoading: false, isProcessing: false });
                    return newObj;
                });

                setAPIError(getUserFacingErrorMessage(error));
                setError(getUserFacingErrorMessage(error));
            }

        } finally {
            console.log('workflow1 stage4');
            // Clear loading for THIS request's conversation (mirrors to the visible UI
            // only if it is the active one — a background finish won't touch your view).
            setLoadingForConversation(streamScope.key, { isLoading: false, isProcessing: false });
            setCurrentRequestId(null);
            // Only clear the shared refs if they still point at THIS request, so a
            // concurrently running background stream is never orphaned.
            if (abortControllerRef.current === controller) abortControllerRef.current = null;
            if (activeRequestRef.current === requestId) activeRequestRef.current = null;
            if (activeStreamConversationKeyRef.current === streamScope.key) activeStreamConversationKeyRef.current = null;
            // Drop this conversation's per-request handles (only if still ours).
            if (streamControllersRef.current.get(streamScope.key) === controller) streamControllersRef.current.delete(streamScope.key);
            if (streamRequestIdRef.current.get(streamScope.key) === requestId) streamRequestIdRef.current.delete(streamScope.key);
            streamEsRef.current.delete(streamScope.key);
            setRequestGuardMessage('');
            if (requestGuardTimerRef.current) {
                clearTimeout(requestGuardTimerRef.current);
                requestGuardTimerRef.current = null;
            }
            if (bottomRef.current) {
                bottomRef.current.scrollIntoView({ behavior: "smooth" });
            }

            console.log(`[LANDINGPAGE] Cleanup completed for request ${requestId}`);
        }
    };

    // MODIFIED stopProcessResearch to close EventSource
    const stopProcessResearch = async () => {
        // Stop ONLY the request for the conversation/tab currently on screen. Other
        // conversations and other browser tabs keep running, untouched.
        const key = activeConversationKeyRef.current;
        try {
            console.log("[LANDINGPAGE] STOP clicked for conversation:", key);

            setStopButton(true);

            const es = (key && streamEsRef.current.get(key)) || null;
            const controller = (key && streamControllersRef.current.get(key)) || null;
            const reqId = (key && streamRequestIdRef.current.get(key)) || currentRequestId;

            // Close ONLY this conversation's EventSource.
            if (es) {
                console.log("[LANDINGPAGE] Closing this conversation's EventSource...");
                es.close();
                if (esRef.current === es) esRef.current = null;
                streamEsRef.current.delete(key);
            }

            // Abort ONLY this conversation's client request.
            if (controller) {
                console.log("[LANDINGPAGE] Aborting this conversation's request...");
                controller.abort();
                if (abortControllerRef.current === controller) abortControllerRef.current = null;
                streamControllersRef.current.delete(key);
            }

            // Tell the server to stop THIS request id only.
            if (reqId) {
                console.log(`[LANDINGPAGE] Sending stop request for ID: ${reqId}`);
                const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                try {
                    const stopResponse = await fetch(`${API_BASE_URL}/api/stop-request`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            requestId: reqId,
                            timestamp: new Date().toISOString(),
                            reason: 'User requested stop'
                        })
                    });

                    if (!stopResponse.ok) {
                        console.warn(`[LANDINGPAGE] Stop request returned status: ${stopResponse.status}`);
                    }

                    const result = await stopResponse.json().catch(() => ({}));

                    if (result.success) {
                        console.log("[LANDINGPAGE] Server confirmed stop:", result.message);
                    } else {
                        console.warn("[LANDINGPAGE] Server stop request failed:", result.message);
                    }
                } catch (stopError) {
                    console.error("[LANDINGPAGE] Failed to send stop request to server:", stopError);
                }
                if (key) streamRequestIdRef.current.delete(key);
            }

            // Clear loading ONLY for the stopped conversation (this mirrors to the visible
            // UI because it is the one being viewed). Background conversations stay as-is.
            setLoadingForConversation(key, { isLoading: false, isProcessing: false });
            setIsLoadingOperator(false);
            setAPIError(null);
            setSearchStatus('');
            setRequestGuardMessage('');
            if (requestGuardTimerRef.current) {
                clearTimeout(requestGuardTimerRef.current);
                requestGuardTimerRef.current = null;
            }

            setThinkingobj((prevState) => {
                const newObj = {
                    ...prevState,
                    error: true,
                    loading: false,
                    stopped: true,
                    stoppedAt: new Date().toISOString(),
                    stoppedMessage: 'Analysis stopped by user',
                    stage: 'Stopped'
                };

                setChatSectionsForConversation(key, (prev) => {
                    return patchOrAppendChatSections(prev, newObj, {
                        error: true,
                        loading: false,
                        stopped: true,
                        stoppedMessage: 'Analysis stopped by user',
                        stage: 'Stopped'
                    });
                });
                saveConversationDraft(key, { thinkingObj: newObj, isLoading: false, isProcessing: false });

                return newObj;
            });

            // Clear the shared "latest" markers only if they pointed at this request.
            if (currentRequestId === reqId) setCurrentRequestId(null);
            if (activeRequestRef.current === reqId) activeRequestRef.current = null;
            if (activeStreamConversationKeyRef.current === key) activeStreamConversationKeyRef.current = null;

            setTimeout(() => {
                setStopButton(false);
            }, 2000);

            console.log("[LANDINGPAGE] Stopped request for conversation:", key);

        } catch (error) {
            console.error("[LANDINGPAGE] Error in stopProcessResearch:", error);

            setLoadingForConversation(key, { isLoading: false, isProcessing: false });
            setIsLoadingOperator(false);
            setAPIError('Failed to stop research process');
            setStopButton(false);
            setRequestGuardMessage('');
        }
    };

    useEffect(() => {
        if (apiError && isLoading) {
            const targetConversationKey = activeStreamConversationKeyRef.current || activeConversationKeyRef.current;
            setThinkingobj((prevState) => {
                const newObj = { ...prevState, timout_err: true };
                setChatSectionsForConversation(targetConversationKey, (prev) => {
                    return patchOrAppendChatSections(prev, newObj, { timout_err: true });
                });
                saveConversationDraft(targetConversationKey, { thinkingObj: newObj });
                return newObj;
            });
        }
    }, [apiError, isLoading]);

    // JSX Rendering starts here
    return (
        <div style={mobileStyles.mainContainer}>
            {showAuthModal && (
                <BasicModal
                    openModal={true}
                    setOpenModal={() => { }}
                    initialView='auth-choice'
                    requireAuth={true}
                />
            )}

            <><TopBar setIsChecked={setIsChecked} /></>
            {isChecked && isLoadingOperator && stopScreenshotUrl && screenshotUrl && (
                <div style={{ display: 'flex', flexDirection: 'row', textAlign: 'center', marginLeft: 'auto', marginRight: 'auto', marginTop: '-40px' }}>
                    <img src={screenshotUrl} style={{ width: isMobile ? '90vw' : '60vw', height: 'auto', border: '1px solid #ccc', borderRadius: '8px', boxShadow: 'rgb(0 0 0 / 10%) 0px 1px 20px 1px' }} />
                </div>
            )}
            {isChecked && isLoadingOperator && stopScreenshotUrl && !screenshotUrl && cachedScreenshotUrl && (
                <div style={{ display: 'flex', flexDirection: 'row', textAlign: 'center', marginLeft: 'auto', marginRight: 'auto', marginTop: '-40px' }}>
                    <img src={cachedScreenshotUrl} style={{ width: isMobile ? '90vw' : '60vw', height: 'auto', border: '1px solid #ccc', borderRadius: '8px', boxShadow: 'rgb(0 0 0 / 10%) 0px 1px 20px 1px' }} />
                </div>
            )}
            {isChecked && isLoadingOperator && !stopScreenshotUrl && (
                <div style={{ width: isMobile ? '90vw' : '60vw', height: isMobile ? '10vh' : '13vh', border: '1px solid #ccc', borderRadius: '8px', boxShadow: 'rgb(0 0 0 / 10%) 0px 1px 20px 1px', display: 'flex', flexDirection: 'row', textAlign: 'center', alignItems: 'center', justifyContent: 'center', margin: '1rem', fontFamily: 'sans-serif' }}>
                    <span style={{ marginRight: '18px' }}><CircularProgress size={isMobile ? "24px" : "30px"} /></span> Loading Screen
                </div>
            )}
            {!isChecked && !hasSubmitted1 && (
                <div style={mobileStyles.heroContainer}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                        <img src={ChainedLM0} style={mobileStyles.heroImage} />
                        <div style={mobileStyles.heroBadge}>
                            Powered by LLMs
                        </div>
                    </div>
                    <h1 style={mobileStyles.heroTitle}>
                        Your AI Powered Specialist Role Profile Builder
                    </h1>
                    <p style={mobileStyles.heroSubtitle}>
                        Do research for role provider in seconds.
                    </p>
                    <HeroShowcase isMobile={isMobile} />
                </div>
            )}

            {!isChecked && (
                <form onSubmit={handleSubmit1} style={{
                    ...mobileStyles.formContainer,
                    marginTop: hasSubmitted1 ? '0' : '2rem'
                }}>
                    {hasSubmitted1 && unpaid && (
                        <div style={{
                            position: 'fixed',
                            bottom: hasSubmitted1 ? (isMobile ? '165px' : '178px') : 'auto',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: '#ffffffff',
                            color: 'black',
                            padding: isMobile ? '6px 12px' : '8px 16px',
                            borderRadius: '6px',
                            fontSize: isMobile ? '12px' : '14px',
                            fontWeight: 500,
                            boxShadow: '0 2px 8px rgba(6, 6, 6, 0.08)',
                            zIndex: 5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            animation: 'slideInFromTop 0.3s ease-out',
                            fontFamily: 'sans-serif'
                        }}>
                            Your Free Tier is exhausted.
                        </div>
                    )}

                    <div style={mobileStyles.formInput}>
                        <textarea
                            value={input}
                            onChange={(e) => {
                                setInput(e.target.value);
                                const isEmpty = e.target.value.length === 0;
                                const minHeight = isEmpty ? (isMobile ? 45 : 60) : (isMobile ? 60 : 92);
                                e.target.style.height = `${minHeight}px`;
                                const newHeight = Math.max(minHeight, Math.min(e.target.scrollHeight, isMobile ? 120 : 200));
                                e.target.style.height = `${newHeight}px`;
                            }}
                            placeholder="Paste a legal job description or search brief. RAGRecruit shortlists matched lawyers by practice, seniority, firm quality, location and mobility, then helps draft AI outreach."
                            style={mobileStyles.textarea}
                        />
                        <div style={{ position: 'absolute', left: isMobile ? '1rem' : '1.9rem', bottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div
                                style={mobileStyles.profileButton}
                                onMouseEnter={() => setShowDeepSearchTooltip(true)}
                                onMouseLeave={() => setShowDeepSearchTooltip(false)}
                            >
                                <span style={{
                                    marginRight: isMobile ? '6px' : '10px',
                                    color: 'rgb(17 24 39 / 58%)',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    <ExploreOutlinedIcon style={{ fontSize: isMobile ? 'small' : 'medium' }} />
                                </span>
                                <span style={{
                                    fontWeight: '550',
                                    color: 'rgb(17 24 39 / 58%)',
                                    marginRight: isMobile ? '4px' : '8px',
                                    fontSize: isMobile ? '0.7rem' : '0.875rem'
                                }}>
                                    {isMobile ? 'Profiles' : 'Profile Suggestions'}
                                </span>
                                <select
                                    value={profileCountChange}
                                    onChange={(e) => setProfileCountChange(e.target.value)}
                                    style={{
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        color: 'rgb(17 24 39 / 58%)',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        outline: 'none',
                                        paddingRight: '5px',
                                        fontSize: isMobile ? '0.7rem' : '0.875rem'
                                    }}
                                >
                                    <option value={1}>1</option>
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={30}>30</option>
                                    <option value={40}>40</option>
                                    <option value={50}>50</option>
                                </select>
                                {showDeepSearchTooltip && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '-40px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                                        color: 'white',
                                        padding: '6px 10px',
                                        borderRadius: '4px',
                                        fontSize: isMobile ? '10px' : '12px',
                                        whiteSpace: 'nowrap',
                                        zIndex: 100,
                                        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)'
                                    }}>
                                        Select how many profile suggestions to generate
                                    </div>
                                )}
                            </div>
                        </div>
                        <div style={{ position: 'absolute', right: isMobile ? '1rem' : '1.9rem', bottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '2rem' }}>
                            {!isLoading && input?.length == 0 && <button type="submit" disabled={true} onMouseEnter={() => setIsSendHovered(true)} onMouseLeave={() => setIsSendHovered(false)} style={{ padding: isMobile ? '10px 11px' : '13px 14px', backgroundColor: input.length > 0 ? 'rgb(17, 24, 39)' : 'white', color: input.length > 0 ? 'white' : 'rgba(145, 145, 145, 1)', border: 'none', borderRadius: '50px', fontWeight: 500, position: 'relative', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.1s ease-in-out', transform: input.length > 0 ? isSendHovered ? 'scale(1.07)' : 'scale(1)' : 'scale(1)' }}>
                                <span style={{ transition: 'transform 0.1s ease-in-out', transform: input.length > 0 ? isSendHovered ? 'scale(1.07)' : 'scale(1)' : 'scale(1)' }}><ArrowUpwardSharpIcon style={{ fontSize: isMobile ? 'small' : 'medium' }} /></span>
                            </button>}
                            {!isLoading && input?.length > 0 && <button type="submit" disabled={isLoading} onMouseEnter={() => setIsSendHovered(true)} onMouseLeave={() => setIsSendHovered(false)} style={{ padding: isMobile ? '10px 11px' : '13px 14px', backgroundColor: input.length > 0 ? 'rgb(17, 24, 39)' : 'white', color: input.length > 0 ? 'white' : 'rgba(145, 145, 145, 1)', border: 'none', borderRadius: '50px', fontWeight: 500, position: 'relative', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.1s ease-in-out', transform: input.length > 0 ? isSendHovered ? 'scale(1.07)' : 'scale(1)' : 'scale(1)' }}>
                                <span style={{ transition: 'transform 0.1s ease-in-out', transform: input.length > 0 ? isSendHovered ? 'scale(1.07)' : 'scale(1)' : 'scale(1)' }}><ArrowUpwardSharpIcon style={{ fontSize: isMobile ? 'small' : 'medium' }} /></span>
                            </button>}
                            {requestGuardMessage && (
                                <span style={{
                                    position: 'absolute',
                                    right: 0,
                                    bottom: isMobile ? '46px' : '52px',
                                    width: isMobile ? '210px' : '270px',
                                    backgroundColor: 'rgba(17, 24, 39, 0.92)',
                                    color: 'white',
                                    fontSize: isMobile ? '10px' : '12px',
                                    lineHeight: 1.35,
                                    padding: '7px 10px',
                                    borderRadius: '6px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                                    textAlign: 'left',
                                    zIndex: 20
                                }}>
                                    {requestGuardMessage}
                                </span>
                            )}
                            {isLoading && (
                                <div
                                    onMouseEnter={() => setIsSendHovered(true)}
                                    onMouseLeave={() => setIsSendHovered(false)}
                                    style={{
                                        textAlign: 'center',
                                        transition: 'transform 0.1s ease-in-out',
                                        transform: isSendHovered ? 'scale(1.07)' : 'scale(1)',
                                        position: 'relative'
                                    }}
                                >
                                    <span
                                        onClick={stopProcessResearch}
                                        style={{
                                            color: stopButton ? '#ff4444' : '#333333',
                                            cursor: 'pointer',
                                            width: '30px',
                                            display: 'inline-block',
                                            transition: 'all 0.2s ease',
                                            animation: stopButton ? 'pulse 0.5s' : 'none'
                                        }}
                                    >
                                        <StopCircleIcon fontSize={isMobile ? 'medium' : 'large'} />
                                    </span>

                                    {stopButton && (
                                        <span style={{
                                            position: 'absolute',
                                            top: '-25px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            fontSize: isMobile ? '10px' : '12px',
                                            color: '#ff4444',
                                            whiteSpace: 'nowrap',
                                            fontWeight: 600,
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            Stopping...
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </form>
            )}

            {isProcessing && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    backgroundColor: '#f0f0f0',
                    zIndex: 9999
                }}>
                    <div style={{
                        height: '100%',
                        backgroundColor: '#121111ff',
                        width: '100%',
                        animation: 'progress 2s ease-in-out infinite'
                    }} />
                </div>
            )}

            {hasSubmitted1 && (
                <div style={{ ...mobileStyles.chatContainer, display: !isChecked ? 'block' : 'none' }}>
                    {chatSections && Array.isArray(chatSections) &&
                        chatSections?.map((section, index) => (
                            <div key={index} style={{ marginBottom: '2rem', borderBottom: '1px solid rgba(75,85,99,0.2)', paddingBottom: '2rem' }}>

                                {section?.iterations?.[0]?.subtopics &&
                                    <div style={mobileStyles.timelineContainer}>
                                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: 0.15, background: 'radial-gradient(circle at 50% 50%, #3a3a3a 0%, #121212 70%)', zIndex: 0 }}></div>

                                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: 0.08, backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '20px 20px', zIndex: 0 }}></div>

                                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: isMobile ? '6px 10px' : '8px 15px', background: 'rgba(0,0,0,0.6)', borderBottom: '1px solid rgba(255,255,255,0.08)', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ fontSize: isMobile ? '10px' : '12px', fontWeight: 'bold', color: '#cccccc', letterSpacing: '0.5px' }}>Research Process</div>

                                            {section?.loading && isLiveSectionForActiveConversation(section) && <div className="processing-text-animation" style={{ fontSize: isMobile ? '9px' : '11px', color: '#999', display: 'flex', alignItems: 'center' }}>
                                                <div style={{ width: isMobile ? '6px' : '8px', height: isMobile ? '6px' : '8px', borderRadius: '50%', backgroundColor: section?.finalSummary ? '#34de34' : '#34de34', marginRight: '6px' }} />
                                                <span >Processing...</span>
                                            </div>}
                                            {section?.error && isSectionForActiveConversation(section) && <div style={{ fontSize: isMobile ? '9px' : '11px', color: '#999', display: 'flex', alignItems: 'center' }}>
                                                <div style={{ width: isMobile ? '6px' : '8px', height: isMobile ? '6px' : '8px', borderRadius: '50%', backgroundColor: section?.finalSummary ? '#34de34' : '#34de34', marginRight: '6px' }} />
                                                <span >Processing Stopped</span>
                                            </div>}
                                            {section?.stage === "Completed" && isSectionForActiveConversation(section) && <div style={{ fontSize: isMobile ? '9px' : '11px', color: '#999', display: 'flex', alignItems: 'center' }}>
                                                <div style={{ width: isMobile ? '6px' : '8px', height: isMobile ? '6px' : '8px', borderRadius: '50%', backgroundColor: section?.finalSummary ? '#34de34' : '#34de34', marginRight: '6px' }} />
                                                <span >Processing Complete</span>
                                            </div>}

                                        </div>

                                        <div style={{ position: 'relative', zIndex: 1, paddingTop: isMobile ? '28px' : '32px' }}>
                                            <Timeline position={isMobile ? "right" : "alternate"}>
                                                <TimelineItem>
                                                    {section?.iterations?.[0]?.subtopics &&
                                                        (!isMobile && <TimelineOppositeContent color="text.secondary">
                                                            <div style={mobileStyles.timelineText}>
                                                                <div style={{ fontWeight: "bold", marginBottom: "4px", color: "#e0e0e0", fontSize: isMobile ? "10px" : "12px" }}>Query Analysis</div>
                                                                <div style={{ fontSize: isMobile ? "9px" : "11px", opacity: 0.8, color: "#cccccc" }}>Processing and understanding the structure of the input query</div>
                                                            </div>
                                                        </TimelineOppositeContent>)
                                                    }
                                                    <TimelineSeparator>
                                                        {section?.iterations?.[0]?.subtopics &&
                                                            <div style={{ position: "relative", zIndex: 2 }}>
                                                                <TimelineDot sx={{ bgcolor: "#aaaaaa", boxShadow: "0 0 5px rgba(170, 170, 170, 0.5)", transform: isMobile ? 'scale(0.8)' : 'scale(1)' }} />
                                                                <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, border: "2px solid rgba(170, 170, 170, 0.3)", borderRadius: "50%", transform: isMobile ? "scale(1.3)" : "scale(1.5)", opacity: 0.6 }}></div>
                                                            </div>
                                                        }
                                                        {section?.iterations?.[0]?.subtopics &&
                                                            <TimelineConnector sx={{ bgcolor: "#aaaaaa", width: "2px" }} />
                                                        }
                                                    </TimelineSeparator>
                                                    {section?.iterations?.[0]?.subtopics &&
                                                        <TimelineContent>
                                                            <div style={{ fontSize: isMobile ? "11px" : "13px", color: "#e0e0e0", fontWeight: "bold", marginBottom: "4px" }}>
                                                                Semantic Decomposition
                                                            </div>
                                                            <div style={{ fontSize: isMobile ? "9px" : "11px", color: "#cccccc" }}>
                                                                Breaking down complex query into core components
                                                            </div>
                                                        </TimelineContent>
                                                    }
                                                </TimelineItem>

                                                {section?.articles?.length > 0 &&
                                                    <TimelineItem>
                                                        {!isMobile && <TimelineOppositeContent color="text.secondary">
                                                            <div style={mobileStyles.timelineText}>
                                                                <div style={{ fontWeight: "bold", marginBottom: "4px", color: "#e0e0e0", fontSize: isMobile ? "10px" : "12px" }}>External Knowledge</div>
                                                                <div style={{ fontSize: isMobile ? "9px" : "11px", opacity: 0.8, color: "#cccccc" }}>Retrieving relevant information from Google search results</div>
                                                            </div>
                                                        </TimelineOppositeContent>}
                                                        <TimelineSeparator>
                                                            <div style={{ position: "relative", zIndex: 2 }}>
                                                                <TimelineDot sx={{ bgcolor: "#999999", boxShadow: "0 0 5px rgba(153, 153, 153, 0.5)", transform: isMobile ? 'scale(0.8)' : 'scale(1)' }} />
                                                                <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, border: "2px solid rgba(153, 153, 153, 0.3)", borderRadius: "50%", transform: isMobile ? "scale(1.3)" : "scale(1.5)", opacity: 0.6 }}></div>
                                                            </div>
                                                            <TimelineConnector sx={{ bgcolor: "#999999", width: "2px" }} />
                                                        </TimelineSeparator>
                                                        <TimelineContent>
                                                            <div style={{ fontSize: isMobile ? "11px" : "13px", color: "#e0e0e0", fontWeight: "bold", marginBottom: "4px" }}>
                                                                Knowledge Retrieval
                                                            </div>
                                                            <div style={{ fontSize: isMobile ? "9px" : "11px", color: "#cccccc" }}>
                                                                Analyzing and extracting insights from sources
                                                            </div>
                                                        </TimelineContent>
                                                    </TimelineItem>
                                                }

                                                <TimelineItem>
                                                    {section?.iterations?.[0]?.initialAnalysis &&
                                                        (!isMobile && <TimelineOppositeContent color="text.secondary">
                                                            <div style={mobileStyles.timelineText}>
                                                                <div style={{ fontWeight: "bold", marginBottom: "4px", color: "#e0e0e0", fontSize: isMobile ? "10px" : "12px" }}>Multi-Model Collaboration</div>
                                                                <div style={{ fontSize: isMobile ? "9px" : "11px", opacity: 0.8, color: "#cccccc" }}>LLMs iteratively refining and cross-validating analysis</div>
                                                            </div>
                                                        </TimelineOppositeContent>)
                                                    }
                                                    <TimelineSeparator>
                                                        {section?.iterations?.[0]?.initialAnalysis &&
                                                            <div style={{ position: "relative", zIndex: 2 }}>
                                                                <TimelineDot sx={{ bgcolor: "#888888", boxShadow: "0 0 5px rgba(136, 136, 136, 0.5)", transform: isMobile ? 'scale(0.8)' : 'scale(1)' }} />
                                                                <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, border: "2px solid rgba(136, 136, 136, 0.3)", borderRadius: "50%", transform: isMobile ? "scale(1.3)" : "scale(1.5)", opacity: 0.6 }}></div>
                                                            </div>
                                                        }
                                                        {section?.iterations?.[0]?.initialAnalysis &&
                                                            <TimelineConnector sx={{ bgcolor: "#888888", width: "2px" }} />
                                                        }
                                                    </TimelineSeparator>
                                                    {section?.iterations?.[0]?.initialAnalysis &&
                                                        <TimelineContent>
                                                            <div style={{ fontSize: isMobile ? "11px" : "13px", color: "#e0e0e0", fontWeight: "bold", marginBottom: "4px" }}>
                                                                Deliberative Process
                                                            </div>
                                                            <div style={{ fontSize: isMobile ? "9px" : "11px", color: "#cccccc" }}>
                                                                Reasoning through contradictions and evidence
                                                            </div>
                                                        </TimelineContent>
                                                    }
                                                </TimelineItem>

                                                <TimelineItem>
                                                    {section?.iterations?.[0]?.finalSummary &&
                                                        (!isMobile && <TimelineOppositeContent color="text.secondary">
                                                            <div style={mobileStyles.timelineText}>
                                                                <div style={{ fontWeight: "bold", marginBottom: "4px", color: "#e0e0e0", fontSize: isMobile ? "10px" : "12px" }}>Response Synthesis</div>
                                                                <div style={{ fontSize: isMobile ? "9px" : "11px", opacity: 0.8, color: "#cccccc" }}>Distilling iterative discussion into coherent output</div>
                                                            </div>
                                                        </TimelineOppositeContent>)
                                                    }
                                                    <TimelineSeparator>
                                                        {section?.iterations?.[0]?.finalSummary &&
                                                            <div style={{ position: "relative", zIndex: 2 }}>
                                                                <TimelineDot sx={{ bgcolor: "#777777", boxShadow: "0 0 5px rgba(119, 119, 119, 0.5)", transform: isMobile ? 'scale(0.8)' : 'scale(1)' }} />
                                                                <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, border: "2px solid rgba(119, 119, 119, 0.3)", borderRadius: "50%", transform: isMobile ? "scale(1.3)" : "scale(1.5)", opacity: 0.6 }}></div>
                                                            </div>
                                                        }
                                                    </TimelineSeparator>
                                                    {section?.iterations?.[0]?.finalSummary &&
                                                        <TimelineContent>
                                                            <div style={{ fontSize: isMobile ? "11px" : "13px", color: "#e0e0e0", fontWeight: "bold", marginBottom: "4px" }}>
                                                                Final Summary
                                                            </div>
                                                            <div style={{ fontSize: isMobile ? "9px" : "11px", color: "#cccccc" }}>
                                                                Comprehensive analysis with high confidence
                                                            </div>
                                                        </TimelineContent>
                                                    }
                                                </TimelineItem>
                                            </Timeline>
                                        </div>

                                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', background: '#1a1a1a', zIndex: 1 }}>
                                            <div style={{ height: '100%', background: 'linear-gradient(90deg, #aaaaaa, #999999, #888888, #777777, #666666)', width: `${section?.finalSummary ? '100%' : section?.iterations?.[0]?.initialAnalysis ? '75%' : section?.articles?.length > 0 ? '50%' : section?.iterations?.[0]?.subtopics ? '25%' : '0%'}` }} />
                                        </div>
                                    </div>
                                }
                                <div style={{ marginTop: '1.5rem' }}>
                                    <p>
                                        <span style={mobileStyles.heading}>
                                            # Query:{' '}
                                            <span style={mobileStyles.textContent}>{section.query || ''}</span>
                                        </span>
                                    </p>
                                </div>

                                {section?.iterations && Object.entries(section?.iterations).map(([iterKey, iter]) => {
                                    const iterNum = parseInt(iterKey);

                                    return (
                                        <div key={iterKey} style={{ paddingTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                            {/* STEP 1: Initial Report - Always render as Markdown now */}
                                            {iterNum === 0 && iter.initialAnalysis && (
                                                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                                    <div style={mobileStyles.messageBubble}>
                                                        <img src={deepseek} style={mobileStyles.avatarLogo} />
                                                        <h4 style={mobileStyles.heading}>
                                                            Step 1: Profile Suggestions (Deepseek)
                                                        </h4>
                                                        {/* Always render as Markdown since we receive raw text */}
                                                        <div className="markdown-content">
                                                            <ReactMarkdown
                                                                remarkPlugins={[remarkGfm, remarkBreaks]}
                                                                components={markdownComponents}
                                                            >
                                                                {iter.initialAnalysis}
                                                            </ReactMarkdown>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* All other steps remain using ReactMarkdown */}
                                            {iterNum === 1 && iter.actorOutput && (
                                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <div style={{ ...mobileStyles.messageBubble, backgroundColor: 'rgba(75,85,99,0.25)', marginRight: '0' }}>
                                                        {section?.actor === 'Deepseek' && <img src={deepseek} style={mobileStyles.avatarLogo} />}
                                                        {section?.actor === 'Google Gemini' && <img src={gemini} style={mobileStyles.avatarLogo} />}
                                                        {section?.actor === 'Anthropic' && <img src={claude} style={mobileStyles.avatarLogo} />}
                                                        <h4 style={mobileStyles.heading}>
                                                            Step 2: Actor Analysis
                                                            {section?.actor === 'Deepseek' && ' (Deepseek)'}
                                                            {section?.actor === 'Google Gemini' && ' (Gemini)'}
                                                            {section?.actor === 'Anthropic' && ' (Claude)'}
                                                            {iter.wasChunked && ` - Processed in ${iter.chunkCount} chunks`}
                                                        </h4>
                                                        <div className="markdown-content">
                                                            <ReactMarkdown
                                                                remarkPlugins={[remarkGfm]}
                                                                components={markdownComponents}
                                                            >
                                                                {iter.actorOutput}
                                                            </ReactMarkdown>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {iterNum === 1 && iter.actorOutput && iter.refinedAnalysis && (
                                                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                                    <div style={{ ...mobileStyles.messageBubble, backgroundColor: 'rgba(75,85,99,0.15)' }}>
                                                        {section?.actor === 'Deepseek' && <img src={deepseek} style={mobileStyles.avatarLogo} />}
                                                        {section?.actor === 'Google Gemini' && <img src={gemini} style={mobileStyles.avatarLogo} />}
                                                        {section?.actor === 'Anthropic' && <img src={claude} style={mobileStyles.avatarLogo} />}
                                                        <h4 style={mobileStyles.heading}>
                                                            Step 3: First Refined Analysis
                                                            {section?.actor === 'Deepseek' && ' (Deepseek)'}
                                                            {section?.actor === 'Google Gemini' && ' (Gemini)'}
                                                            {section?.actor === 'Anthropic' && ' (Claude)'}
                                                        </h4>
                                                        <div className="markdown-content">
                                                            <ReactMarkdown
                                                                remarkPlugins={[remarkGfm]}
                                                                components={markdownComponents}
                                                            >
                                                                {iter.refinedAnalysis}
                                                            </ReactMarkdown>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {iterNum === 1 && iter.actorOutput && iter.refinedAnalysis && iter.criticFeedback && (
                                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <div style={{ ...mobileStyles.messageBubble, backgroundColor: 'rgba(75,85,99,0.25)', marginRight: '0' }}>
                                                        {section?.critic === 'Deepseek' && <img src={deepseek} style={mobileStyles.avatarLogo} />}
                                                        {section?.critic === 'Google Gemini' && <img src={gemini} style={mobileStyles.avatarLogo} />}
                                                        {section?.critic === 'Anthropic' && <img src={claude} style={mobileStyles.avatarLogo} />}
                                                        <h4 style={mobileStyles.heading}>
                                                            Step 4: First Critic Feedback
                                                            {section?.critic === 'Deepseek' && ' (Deepseek)'}
                                                            {section?.critic === 'Google Gemini' && ' (Gemini)'}
                                                            {section?.critic === 'Anthropic' && ' (Claude)'}
                                                        </h4>
                                                        <div className="markdown-content">
                                                            <ReactMarkdown
                                                                remarkPlugins={[remarkGfm]}
                                                                components={markdownComponents}
                                                            >
                                                                {iter.criticFeedback}
                                                            </ReactMarkdown>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {iterNum === 2 && iter.actorOutput && (
                                                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                                    <div style={{ ...mobileStyles.messageBubble, backgroundColor: 'rgba(75,85,99,0.15)' }}>
                                                        {section?.actor === 'Deepseek' && <img src={deepseek} style={mobileStyles.avatarLogo} />}
                                                        {section?.actor === 'Google Gemini' && <img src={gemini} style={mobileStyles.avatarLogo} />}
                                                        {section?.actor === 'Anthropic' && <img src={claude} style={mobileStyles.avatarLogo} />}
                                                        <h4 style={mobileStyles.heading}>
                                                            Step 5: Second Refined Analysis
                                                            {section?.actor === 'Deepseek' && ' (Deepseek)'}
                                                            {section?.actor === 'Google Gemini' && ' (Gemini)'}
                                                            {section?.actor === 'Anthropic' && ' (Claude)'}
                                                        </h4>
                                                        <div className="markdown-content">
                                                            <ReactMarkdown
                                                                remarkPlugins={[remarkGfm]}
                                                                components={markdownComponents}
                                                            >
                                                                {iter.actorOutput}
                                                            </ReactMarkdown>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {iterNum === 2 && iter.criticFeedback && (
                                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <div style={{ ...mobileStyles.messageBubble, backgroundColor: 'rgba(75,85,99,0.25)', marginRight: '0' }}>
                                                        {section?.critic === 'Deepseek' && <img src={deepseek} style={mobileStyles.avatarLogo} />}
                                                        {section?.critic === 'Google Gemini' && <img src={gemini} style={mobileStyles.avatarLogo} />}
                                                        {section?.critic === 'Anthropic' && <img src={claude} style={mobileStyles.avatarLogo} />}
                                                        <h4 style={mobileStyles.heading}>
                                                            Step 6: Second Critic Feedback
                                                            {section?.critic === 'Deepseek' && ' (Deepseek)'}
                                                            {section?.critic === 'Google Gemini' && ' (Gemini)'}
                                                            {section?.critic === 'Anthropic' && ' (Claude)'}
                                                        </h4>
                                                        <div className="markdown-content">
                                                            <ReactMarkdown
                                                                remarkPlugins={[remarkGfm]}
                                                                components={markdownComponents}
                                                            >
                                                                {iter.criticFeedback}
                                                            </ReactMarkdown>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {iterNum === 2 && iter.finalSummary && (
                                                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                                    <div style={{ ...mobileStyles.messageBubble, backgroundColor: 'rgba(75,85,99,0.15)' }}>
                                                        {section?.actor === 'Deepseek' && <img src={deepseek} style={mobileStyles.avatarLogo} />}
                                                        {section?.actor === 'Google Gemini' && <img src={gemini} style={mobileStyles.avatarLogo} />}
                                                        {section?.actor === 'Anthropic' && <img src={claude} style={mobileStyles.avatarLogo} />}
                                                        <h4 style={mobileStyles.heading}>
                                                            Step 7: Final Summary
                                                            {section?.actor === 'Deepseek' && ' (Deepseek)'}
                                                            {section?.actor === 'Google Gemini' && ' (Gemini)'}
                                                            {section?.actor === 'Anthropic' && ' (Claude)'}
                                                        </h4>
                                                        <div className="markdown-content">
                                                            <ReactMarkdown
                                                                remarkPlugins={[remarkGfm]}
                                                                components={markdownComponents}
                                                            >
                                                                {iter.finalSummary}
                                                            </ReactMarkdown>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {section?.error && !section?.stopped && isSectionForActiveConversation(section) && (
                                    <div style={{ paddingTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                                        <div style={{ maxWidth: '60%', backgroundColor: 'rgba(75,85,99,0.2)', borderRadius: '0.75rem', padding: isMobile ? '0.75rem' : '1rem', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', borderTopRightRadius: '0.25rem', marginLeft: 'auto', marginRight: '0', alignItems: "center", justifyContent: 'flex-end', marginRight: "5px" }}>
                                            <span style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", marginRight: "5px" }}>
                                                {section.error === true && <ErrorOutlineOutlinedIcon color="grey" style={{ fontSize: isMobile ? "small" : "medium" }} />}
                                                <h4 style={{ ...mobileStyles.heading, marginRight: '5px', marginLeft: '10px', fontSize: isMobile ? '14px' : '16.5px' }}>Stage:</h4>
                                                <p style={mobileStyles.textContent}>
                                                    {section.errorMessage || section.stage || 'An error occurred'}
                                                </p>
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {section?.loading && isLiveSectionForActiveConversation(section) && (
                                    <div style={{ paddingTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                                        <div style={{ maxWidth: '60%', backgroundColor: 'rgba(75,85,99,0.2)', borderRadius: '0.75rem', padding: isMobile ? '0.75rem' : '1rem', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', borderTopRightRadius: '0.25rem', marginLeft: 'auto', marginRight: '0', alignItems: "center", justifyContent: 'flex-end', marginRight: "5px" }}>
                                            <span style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", marginRight: "5px" }}>
                                                {section.stage !== "Completed" && <CircularProgress color="grey" size={isMobile ? "14px" : "16px"} />}
                                                {section.stage === "Completed" && <DoneIcon color="grey" style={{ fontSize: isMobile ? "small" : "medium" }} />}
                                                <h4 style={{ ...mobileStyles.heading, marginRight: '5px', marginLeft: '10px', fontSize: isMobile ? '14px' : '16.5px' }}>Stage:</h4>
                                                <p style={mobileStyles.textContent}>{section.stage}</p>
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {section?.stage === "Completed" && isSectionForActiveConversation(section) && (
                                    <div style={{ paddingTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                                        <div style={{ maxWidth: '60%', backgroundColor: 'rgba(75,85,99,0.2)', borderRadius: '0.75rem', padding: isMobile ? '0.75rem' : '1rem', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', borderTopRightRadius: '0.25rem', marginLeft: 'auto', marginRight: '0', alignItems: "center", justifyContent: 'flex-end', marginRight: "5px" }}>
                                            <span style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", marginRight: "5px" }}>
                                                {section.stage !== "Completed" && <CircularProgress color="grey" size={isMobile ? "14px" : "16px"} />}
                                                {section.stage === "Completed" && <DoneIcon color="grey" style={{ fontSize: isMobile ? "small" : "medium" }} />}
                                                <h4 style={{ ...mobileStyles.heading, marginRight: '5px', marginLeft: '10px', fontSize: isMobile ? '14px' : '16.5px' }}>Stage:</h4>
                                                <p style={mobileStyles.textContent}>{section.stage}</p>
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Inline Gmail-style outreach for this shortlist — show whenever the
                                    section has produced a final report (covers restored chats whose
                                    stage isn't re-marked "Completed"). iterations may be an array or
                                    an index-keyed object, so normalise before checking. */}
                                {(() => {
                                    // Only after the server has finished the complete list.
                                    if (!isSectionForActiveConversation(section) || !section?.query) return null;
                                    if (section?.loading) return null;
                                    const its = section?.iterations;
                                    const arr = Array.isArray(its) ? its : (its && typeof its === 'object' ? Object.values(its) : []);
                                    const reportText = String(section?.finalSummary || '') +
                                        arr.map((it) => String((it && (it.finalSummary || it.initialAnalysis)) || '')).join('');
                                    const candidates = Array.isArray(section?.shortlistCandidates) ? section.shortlistCandidates : null;
                                    const hasReport = reportText.length > 150;
                                    if (!hasReport && !(candidates && candidates.length)) return null;
                                    return (
                                        <InlineOutreach
                                            roleQuery={section.query}
                                            roleTitle={section.query}
                                            candidates={candidates}
                                            reportText={reportText}
                                        />
                                    );
                                })()}
                            </div>
                        ))}
                    {/* Spacer so the last content (e.g. the inline outreach panel) can scroll
                        clear of the fixed search input that floats over the bottom ~24vh. */}
                    <div ref={bottomRef} style={{ height: isMobile ? '23vh' : '26vh', minHeight: 160 }} />
                </div>
            )}
            <span style={mobileStyles.fabButton}>
                <Fab aria-label="downIcon" onClick={() => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })} size={isMobile ? "small" : "medium"}>
                    <ExpandMoreIcon />
                </Fab>
            </span>
        </div>
    );
}

export default Landingpage;
